import React, { useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { EXCLUDED_EMOJI_IDS } from '../../../../constants/main';

const pickerWrapperStyle = {
  position: 'absolute',
  zIndex: 1000,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  borderRadius: '8px',
  overflow: 'hidden'
};

const RichTextEditorEmojiPicker = ({
  isOpen,
  position,
  onClickOutside,
  onEmojiSelect,
  toolbarButtonSelector = '',
  containerSelector = 'body',
  quill
}) => {
  const dropdownRef = useRef(null);
  const justOpenedRef = useRef(false);

  // Handle opening state with a short delay to prevent closing on the same click
  useEffect(() => {
    if (isOpen) {
      justOpenedRef.current = true;
      const timer = setTimeout(() => {
        justOpenedRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      // Find the web component element inside the wrapper
      const pickerElement = dropdownRef.current.querySelector('em-emoji-picker');

      // Access the shadow root and inject styles because of lack
      // of props for the Picker element
      if (pickerElement && pickerElement.shadowRoot) {
        const style = document.createElement('style');
        style.textContent = `
          #root {
            height: 255px !important;
            max-height: 255px !important;

            /* Hide the category titles */
            .category > :first-child {
            display: none !important;
          }
          }
        `;
        pickerElement.shadowRoot.appendChild(style);
      }
    }
  }, [isOpen]);

  // Handle clicking outside
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (justOpenedRef.current) {
        return;
      }

      const isInsideDropdown = dropdownRef.current
        && dropdownRef.current.contains(event.target);

      const isToolbarButton = event.target.closest('.ql-toolbar button')?.classList?.contains(toolbarButtonSelector);
      const isInsideToolbarModal = event.target.closest('.ant-modal')?.classList?.contains('ql-modal');
      const isInsideToolbarPopover = event.target.closest('.ant-popover')?.classList?.contains('ql-custom-link-popover');
      if (!isInsideDropdown && !isInsideToolbarModal && !isInsideToolbarPopover && !isToolbarButton) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClickOutside, toolbarButtonSelector]);

  const container = useMemo(() => {
    if(quill?.editor?.container) {
      return quill.editor.container;
    }
    return containerSelector === 'body'
      ? document.body
      : document.querySelector(containerSelector);
  }, [containerSelector, quill]);

  if (!isOpen) {
    return null;
  }

  const handleEmojiClick = (emoji) => {
    onEmojiSelect(emoji.native);
  };

  return createPortal(
    <div
      id="emoji-pallete"
      ref={dropdownRef}
      style={{
        ...pickerWrapperStyle,
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <Picker
        data={data}
        theme="light"
        onEmojiSelect={handleEmojiClick}
        exceptEmojis={EXCLUDED_EMOJI_IDS}
        perLine={7}
        emojiSize={20}
        previewPosition="none"
        skinTonePosition="none"
        maxFrequentRows={0}
        emojiButtonSize={28}
        searchPosition="none"
        navPosition="top"
      />
    </div>,
    container
  );
};

RichTextEditorEmojiPicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  position: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired
  }).isRequired,
  onEmojiSelect: PropTypes.func,
  onClickOutside: PropTypes.func.isRequired,
  toolbarButtonSelector: PropTypes.string,
  containerSelector: PropTypes.string,
  quill: PropTypes.object
};

export default RichTextEditorEmojiPicker;
